'use client'

import { useCallback, useRef } from 'react'

import { HorizontalScrollControls } from '@/components/HorizontalScrollControls'
import { Kicker } from '@/components/Kicker'
import { Media } from '@/components/Media'
import { RevealSection } from '@/components/RevealSection'
import RichText from '@/components/RichText'
import { useLenisContext } from '@/providers/Lenis'
import { cn } from '@/utilities/ui'

import type { CardStack as CardStackType } from '@/payload-types'

const getColorScheme = (scheme: string) => {
  if (scheme === 'accent-3') return 'outback-600'
  return scheme
}

export const CardStack: React.FC<CardStackType> = ({ header, cards }) => {
  const { isMobile } = useLenisContext()
  const { mainHeading, kicker, copy } = header || {}

  const cardStackRef = useRef<HTMLDivElement>(null)
  const scrollContainerRef = useRef<HTMLDivElement>(null)
  const cardStackItemsRefs = useRef<(HTMLDivElement | null)[]>([])
  const cardStackTitleRefs = useRef<(HTMLLIElement | null)[]>([])

  const handleItemRef = useCallback(
    (
      itemRefs: React.RefObject<(HTMLDivElement | HTMLLIElement | null)[]>,
      el: HTMLDivElement | HTMLLIElement | null,
      index: number,
    ) => {
      itemRefs.current[index] = el
    },
    [],
  )

  return (
    <RevealSection threshold={0.01} className="relative md:pb-8 lg:pb-12">
      <div
        ref={cardStackRef}
        className="wrapper grid gap-6 text-left lg:grid-cols-[minmax(0,40%)_minmax(0,60%)] lg:gap-8 xl:gap-14"
      >
        <div className="flex flex-col gap-3">
          <h2 className="flex flex-col items-start justify-start gap-2 font-display text-3xl font-normal text-foreground lg:text-4xl">
            {kicker && <Kicker className="font-body font-normal">{kicker}</Kicker>}
            {mainHeading}
          </h2>
          <RichText
            data={copy}
            enableGutter={false}
            className="font-body text-base font-normal text-card-foreground xl:text-lg"
          />
          <ul className="list-disc space-y-2 pl-4 max-lg:hidden">
            {cards?.map((card: CardStackType['cards'][number], index: number) => {
              const { cardStackItem } = card
              return (
                <li
                  key={`${card.id}-title`}
                  ref={(el) => handleItemRef(cardStackTitleRefs, el, index)}
                  className={cn(
                    'origin-left font-body text-base font-normal text-balance text-card-foreground transition-all xl:text-lg',
                    `card-stack-list-title-${index}`,
                  )}
                >
                  {cardStackItem.title}
                </li>
              )
            })}
          </ul>
        </div>
        <div className="w-full min-w-0">
          <div
            ref={scrollContainerRef}
            className={cn(
              'scrollbar-hidden -mr-[max(1rem,calc((100vw-var(--breakpoint-wrapper))/2+1rem))] flex snap-x snap-mandatory flex-row gap-3 overflow-x-auto pr-8 md:gap-6',
              'min-w-0 scroll-pl-3 md:scroll-pl-0',
              'outline-none focus-visible:ring-2 focus-visible:ring-evergreen-500 focus-visible:ring-offset-2 focus-visible:ring-offset-background',
            )}
            data-lenis-prevent-horizontal
          >
            {cards?.map((card: CardStackType['cards'][number], index: number) => {
              const { cardStackItem } = card
              const colorScheme = cardStackItem.colorScheme
              return (
                <div
                  key={`${card.id}-item`}
                  ref={(el) => handleItemRef(cardStackItemsRefs, el, index)}
                  data-index={index}
                  className={cn(
                    'card-stack-item relative flex aspect-8/5 h-full flex-col justify-end overflow-hidden rounded-2xl p-4 text-left md:p-6 lg:p-8',
                    'w-[calc(100vw-3rem)] min-w-0 shrink-0 snap-start md:w-auto md:max-w-125 xl:max-w-150',
                  )}
                  style={{ zIndex: index }}
                >
                  <div className="absolute inset-0 overflow-hidden rounded-2xl">
                    {cardStackItem.image && (
                      <Media
                        fill={!isMobile}
                        resource={cardStackItem.image}
                        context={'thumbnail'}
                        htmlelement={null}
                        pictureClassName="relative z-0 block size-full object-cover object-center"
                        imgClassName="size-full object-cover object-center"
                      />
                    )}
                    <div
                      className={cn('absolute inset-x-0 bottom-0 z-1 size-full')}
                      style={{
                        backgroundImage: `linear-gradient(to bottom, transparent ${isMobile ? '0%' : '25%'}, var(--${getColorScheme(colorScheme)}) 100%)`,
                      }}
                    />
                  </div>
                  <div className="relative z-2">
                    <h3 className="card-stack-item-title font-body text-base font-semibold text-balance text-background drop-shadow-md md:text-lg lg:text-2xl">
                      {cardStackItem.title}
                    </h3>
                    {cardStackItem.copy && (
                      <p className="card-stack-item-copy mt-2 font-body text-sm text-background drop-shadow-sm md:text-base lg:mt-3 lg:text-base">
                        {cardStackItem.copy}
                      </p>
                    )}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
      <HorizontalScrollControls
        scrollRef={scrollContainerRef as React.RefObject<HTMLDivElement>}
        className="wrapper mt-4 flex justify-end"
      />
    </RevealSection>
  )
}
